import {
  Component,
  OnInit,
  OnDestroy,
  signal,
  computed,
  ChangeDetectorRef,
} from '@angular/core';
import { DetailsCardComponent } from '../../../components/details-card/details-card.component';
import {
  QaiLinkButtonComponent,
  QaiPairedFieldsComponent,
  type PairedFieldItem,
} from '@qai/ui';
import { ActivatedRoute } from '@angular/router';
import { AuthorService } from 'src/app/services/self-serve/author.service';
import { CallWordType } from 'src/app/types/call-word.types';
import { FormGroup, FormArray, FormControl, FormBuilder } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { AuthorReactiveFormService } from '../../../services/author-reactive-form.service';
import { SimulationConstants } from 'src/app/utils/constants/self-serve/simulation.constants';
import {
  take,
  debounceTime,
  distinctUntilChanged,
  Subscription,
  skip,
  switchMap,
  of,
  tap,
} from 'rxjs';
import { CommonModule } from '@angular/common';
import { IWordDefinition } from 'src/app/interfaces/self-serve/responses/iword-definition';
import { SaveAsProductDefaultModalComponent } from '../../../components/modals/save-as-product-default-modal/save-as-product-default-modal.component';
import { IWordsRequest } from 'src/app/interfaces/self-serve/requests/iwords-request';
import { IGetWordDetailsResponse } from 'src/app/interfaces/self-serve/responses/iget-word-details-response';
import { detectPairedFieldChanges } from '../keyword-change-detection.utils';

import { TranslocoModule } from '@jsverse/transloco';

@Component({
  selector: 'app-replace-words',
  imports: [
    TranslocoModule,
    DetailsCardComponent,
    QaiLinkButtonComponent,
    QaiPairedFieldsComponent,
    CommonModule,
    ReactiveFormsModule,
    SaveAsProductDefaultModalComponent,
  ],
  templateUrl: './replace-words.component.html',
  styleUrl: './replace-words.component.scss',
})
export class ReplaceWordsComponent implements OnInit, OnDestroy {
  title: string = 'simulationBuilder.KEYWORDS_REPLACE_TITLE';
  description: string =
    'simulationBuilder.KEYWORDS_REPLACE_DESCRIPTION';
  fromLabel: string = 'simulationBuilder.KEYWORDS_FROM_LABEL';
  toLabel: string = 'simulationBuilder.KEYWORDS_TO_LABEL';
  addFromText: string = 'simulationBuilder.KEYWORDS_ADD_REPLACEMENT';
  addNewPairText: string = 'simulationBuilder.KEYWORDS_ADD_NEW_WORD';
  callUuid: string = '';
  moduleUuid: string = '';
  personaUuid: string = '';
  form!: FormGroup;
  replaceWordsPairedFieldsControl: FormControl<PairedFieldItem[]> =
    new FormControl<PairedFieldItem[]>([], {
      nonNullable: true,
    });
  private replaceWordsSubscription?: Subscription;
  private replaceWordsPairedFieldsControlSubscription?: Subscription;
  private isUpdatingFromFormArray = signal(false);
  private isUpdatingFromAPI = signal(false);
  private wordTypeUuid: string = '';
  private previousPairedFields: PairedFieldItem[] = [];
  private inFlightCreateRequests = new Set<string>();
  private apiErrors = new Map<string, { key?: string; value?: string }>();
  private duplicateKeyValuePairs = new Set<string>();
  private readonly ERROR_DUPLICATE_KEY_VALUE =
    'Duplicate key combinations are not allowed';
  private readonly DUPLICATE_WORD_STATUS = 409;

  saveAsProductDefaultModalOpen: boolean = false;
  keywordTitle: string = 'simulationBuilder.KEYWORDS_REPLACE_NAME';
  networkRequestInProgress: boolean = false;
  private apiRequestCount = signal(0);
  private deleteApiCount = signal(0);
  private createUpdateApiCount = signal(0);
  private hasPendingWordChange = signal(false);
  private hasPendingDeleteChange = signal(false);
  private hasPendingAddChange = signal(false);
  readonly isApiRequestInProgress = computed(
    () => this.apiRequestCount() > 0 || this.hasPendingWordChange()
  );
  readonly isDeleteInProgress = computed(
    () => this.deleteApiCount() > 0 || this.hasPendingDeleteChange()
  );
  readonly isCreateUpdateInProgress = computed(
    () => this.createUpdateApiCount() > 0 || this.hasPendingAddChange()
  );

  constructor(
    private authorService: AuthorService,
    private route: ActivatedRoute,
    private authorReactiveFormService: AuthorReactiveFormService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {}

  get replaceWordsFormArray(): FormArray {
    return this.form?.get(
      SimulationConstants.REPLACE_WORDS_SECTION
    ) as FormArray;
  }

  get hasNoWords(): boolean {
    const wordItems = this.replaceWordsPairedFieldsControl?.value || [];
    return (
      wordItems.length === 0 ||
      wordItems.every((item) => !item.key || item.key.trim().length === 0)
    );
  }

  get hasErrors(): boolean {
    return this.apiErrors.size > 0;
  }

  get allWordsHaveProductScope(): boolean {
    if (!this.replaceWordsFormArray || this.hasNoWords) {
      return false;
    }
    const words = this.replaceWordsFormArray.getRawValue() as IWordDefinition[];
    if (words.length === 0) {
      return false;
    }
    return words.every(
      (word) => word?.scope === SimulationConstants.KEYWORDS_PRODUCT_SCOPE
    );
  }

  getFieldError = (
    item: PairedFieldItem,
    index: number,
    field: 'key' | 'value'
  ): string | undefined => {
    const itemId =
      item.uuid ||
      `${item.key?.trim()}|${item.value?.trim()}` ||
      `index-${index}`;
    const apiError = this.apiErrors.get(itemId);

    if (apiError) {
      return field === 'key' ? apiError.key : apiError.value;
    }

    return undefined;
  };

  private updateFormControlDisabledState(): void {
    const shouldDisable = !this.hasNoWords && this.allWordsHaveProductScope;
    const wasDisabled = this.replaceWordsPairedFieldsControl.disabled;
    
    if (shouldDisable) {
      if (!wasDisabled) {
        this.replaceWordsPairedFieldsControl.disable({ emitEvent: false });
      }
    } else {
      if (wasDisabled) {
        const items = this.replaceWordsFormArray?.getRawValue() as IWordDefinition[] || [];
        const validItems = items.filter(
          (item) => item.key && item.key.trim().length > 0
        );
        const expectedPairedFieldItems = validItems.map((item) => ({
          key: item.key || '',
          value: item.value || '',
          uuid: item.definitionUuid || undefined,
        }));

        if (expectedPairedFieldItems.length > 0) {
          this.isUpdatingFromFormArray.set(true);
          this.replaceWordsPairedFieldsControl.setValue(expectedPairedFieldItems, {
            emitEvent: false,
          });
          this.isUpdatingFromFormArray.set(false);
        }

        this.replaceWordsPairedFieldsControl.enable({ emitEvent: false });
        
        if (expectedPairedFieldItems.length > 0) {
          this.isUpdatingFromFormArray.set(true);
          this.replaceWordsPairedFieldsControl.setValue(expectedPairedFieldItems, {
            emitEvent: false,
          });
          this.isUpdatingFromFormArray.set(false);
        }
      }
    }
  }

  get hasProductScopedWords(): boolean {
    const hasProductScopedWordsControl = this.form?.get(
      'replace_words_hasProductScopedWords'
    );
    return hasProductScopedWordsControl?.value ?? false;
  }

  ngOnInit(): void {
    this.callUuid = this.route.snapshot.paramMap.get('uuid') || '';
    this.loadPersonaUuid();
    this.loadModuleUuid();
    this.loadReplaceWords();
  }

  private loadReplaceWords(): void {
    this.authorReactiveFormService
      .getForm(this.callUuid)
      .pipe(take(1))
      .subscribe((form) => {
        this.form = form.get(SimulationConstants.KEYWORDS_SECTION) as FormGroup;

        const wordTypeUuidControl = this.form?.get(
          'replace_words_wordTypeUuid'
        );
        if (wordTypeUuidControl) {
          this.wordTypeUuid = wordTypeUuidControl.value;
        }

        if (this.replaceWordsFormArray) {
          this.syncFormArrayToControl();
          this.previousPairedFields =
            this.replaceWordsPairedFieldsControl.value || [];
          this.setupSyncBetweenFormArrayAndControl();
          this.updateFormControlDisabledState();
        }
      });
  }

  private syncFormArrayToControl(): void {
    if (!this.replaceWordsFormArray) return;

    const items = this.replaceWordsFormArray.getRawValue() as IWordDefinition[];

    // Filter out entries without key or with empty/whitespace key
    const validItems = items.filter(
      (item) => item.key && item.key.trim().length > 0
    );

    // Convert valid IWordDefinition[] to PairedFieldItem[]
    const pairedFieldItems = validItems.map((item) => ({
      key: item.key || '',
      value: item.value || '',
      uuid: item.definitionUuid || undefined,
    }));

    this.isUpdatingFromFormArray.set(true);
    this.replaceWordsPairedFieldsControl.setValue(pairedFieldItems, {
      emitEvent: false,
    });
    this.isUpdatingFromFormArray.set(false);
  }

  private syncPairedFieldsToFormArray(
    pairedFieldItems: PairedFieldItem[],
    skipApiProcessing: boolean = false
  ): void {
    if (!this.replaceWordsFormArray) return;
    this.authorReactiveFormService.syncPairedFieldsToFormArray(
      this.replaceWordsFormArray,
      pairedFieldItems,
      SimulationConstants.KEYWORDS_DEFAULT_SCOPE,
      SimulationConstants.REPLACE_WORDS_SECTION,
      this.isUpdatingFromFormArray
    );

    this.cdr.detectChanges();

    if (!skipApiProcessing) {
      requestAnimationFrame(() => {
        if (!this.isUpdatingFromAPI()) {
          this.processReplaceWordsForAPI();
        }
      });
    }
  }

  private setupSyncBetweenFormArrayAndControl(): void {
    if (this.replaceWordsSubscription) {
      this.replaceWordsSubscription.unsubscribe();
    }
    if (this.replaceWordsPairedFieldsControlSubscription) {
      this.replaceWordsPairedFieldsControlSubscription.unsubscribe();
    }

    this.replaceWordsSubscription = this.replaceWordsFormArray.valueChanges
      .pipe(
        skip(1),
        debounceTime(SimulationConstants.AUTOSAVE_GENERIC_DEBOUNCE),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      )
      .subscribe(() => {
        if (!this.replaceWordsFormArray) return;

        if (this.isUpdatingFromAPI()) {
          return;
        }

        if (this.isUpdatingFromFormArray()) {
          return;
        }

        this.personaUuid = this.getPersonaUuidFromForm();

        this.syncFormArrayToControl();
        this.updateFormControlDisabledState();
        this.processReplaceWordsForAPI();
      });

    this.replaceWordsPairedFieldsControlSubscription =
      this.replaceWordsPairedFieldsControl.valueChanges
        .pipe(
          tap((currentValue: PairedFieldItem[]) => {
            if (!this.isUpdatingFromAPI() && !this.isUpdatingFromFormArray()) {
              const { hasDeletion, hasCompleteNewItem, hasUpdatedItem, hasMeaningfulChange } =
                detectPairedFieldChanges(this.previousPairedFields, currentValue);
              if (hasMeaningfulChange) {
                this.hasPendingWordChange.set(true);
              }
              if (hasDeletion) {
                this.hasPendingDeleteChange.set(true);
              }
              if (hasCompleteNewItem || hasUpdatedItem) {
                this.hasPendingAddChange.set(true);
              }
            }
          }),
          debounceTime(SimulationConstants.AUTOSAVE_GENERIC_DEBOUNCE),
          tap(() => {
            this.hasPendingWordChange.set(false);
            this.hasPendingDeleteChange.set(false);
            this.hasPendingAddChange.set(false);
          }),
          distinctUntilChanged(
            (a, b) => JSON.stringify(a) === JSON.stringify(b)
          )
        )
        .subscribe((pairedFieldItems: PairedFieldItem[]) => {
          if (this.isUpdatingFromAPI()) {
            this.previousPairedFields = pairedFieldItems;
            return;
          }
          if (this.isUpdatingFromFormArray()) {
            this.previousPairedFields = pairedFieldItems;
            return;
          }
          if (JSON.stringify(pairedFieldItems) === JSON.stringify(this.previousPairedFields)) {
            return;
          }

          if (
            pairedFieldItems.length === 1 &&
            (!pairedFieldItems[0].key || pairedFieldItems[0].key.trim().length === 0) &&
            (!pairedFieldItems[0].value || pairedFieldItems[0].value.trim().length === 0)
          ) {
            const formArrayWords =
              (this.replaceWordsFormArray?.getRawValue() as IWordDefinition[]) || [];
            const hasApiWords = formArrayWords.some((word) => word?.definitionUuid);
            if (hasApiWords) {
              this.syncFormArrayToControl();
            }
            return;
          }

          if (this.apiErrors.size > 0) {
            // Detect removals before the error-state sync rebuilds the FormArray, else a persisted row is dropped without calling the delete API
            const removedWhileErrored = this.detectRemovals(
              this.previousPairedFields,
              pairedFieldItems
            );
            this.previousPairedFields = pairedFieldItems;
            this.clearErrorsForModifiedFields(pairedFieldItems);

            if (removedWhileErrored && removedWhileErrored.length > 0) {
              this.onItemsRemoved(removedWhileErrored);
              this.updateFormControlDisabledState();
              return;
            }

            this.syncPairedFieldsToFormArray(pairedFieldItems, true);
            this.validateAndUpdateErrors();

            if (this.apiErrors.size === 0) {
              this.replaceWordsPairedFieldsControl.updateValueAndValidity();
              this.updateFormControlDisabledState();
            } else {
              return;
            }
          }

          const hasIncompleteFields = pairedFieldItems.some(
            (item) =>
              !item.key ||
              item.key.trim().length === 0 ||
              !item.value ||
              item.value.trim().length === 0
          );

          if (hasIncompleteFields) {
            this.previousPairedFields = pairedFieldItems;
            this.syncPairedFieldsToFormArray(pairedFieldItems, true);
            this.updateFormControlDisabledState();
            return;
          }

          this.personaUuid = this.getPersonaUuidFromForm();

          const removedItems = this.detectRemovals(
            this.previousPairedFields,
            pairedFieldItems
          );

          if (removedItems && removedItems.length > 0) {
            this.onItemsRemoved(removedItems);
            this.previousPairedFields = pairedFieldItems;
            this.updateFormControlDisabledState();
            return;
          }

          this.previousPairedFields = pairedFieldItems;
          this.syncPairedFieldsToFormArray(pairedFieldItems);
          this.updateFormControlDisabledState();
        });
  }

  private getPersonaUuidFromForm(): string {
    if (!this.form) return '';
    const generalSection = this.form.parent?.get(
      SimulationConstants.GENERAL_SECTION
    ) as FormGroup;
    const callPersonaGroup = generalSection?.get(
      SimulationConstants.CALL_PERSONA_SECTION
    ) as FormGroup;
    const personaUuidControl = callPersonaGroup?.get('uuid');
    return personaUuidControl?.value || '';
  }

  private getScopeUuid(scope: string): string {
    switch (scope) {
      case SimulationConstants.KEYWORDS_PRODUCT_SCOPE:
        return this.moduleUuid;
      case SimulationConstants.KEYWORDS_PERSONA_SCOPE:
        return this.getPersonaUuidFromForm();
      case SimulationConstants.KEYWORDS_DEFAULT_SCOPE:
      default:
        return this.callUuid;
    }
  }

  private transformWordsToRequest(
    words: IWordDefinition[],
    includeUuid: boolean = false
  ): IWordsRequest {
    // Only include words with both key and value (complete pairs)
    const validWords = words.filter(
      (word) =>
        word.key &&
        word.key.trim().length > 0 &&
        word.value &&
        word.value.trim().length > 0
    );

    const filteredWords = includeUuid
      ? validWords.filter(
          (word) => word.definitionUuid && word.definitionUuid.trim().length > 0
        )
      : validWords;

    let wordTypeUuidToUse = this.wordTypeUuid;
    if (!wordTypeUuidToUse || wordTypeUuidToUse.trim().length === 0) {
      const wordTypeUuidControl = this.form?.get('replace_words_wordTypeUuid');
      wordTypeUuidToUse = wordTypeUuidControl?.value || '';
    }
    if (!wordTypeUuidToUse || wordTypeUuidToUse.trim().length === 0) {
      wordTypeUuidToUse =
        filteredWords.find(
          (word) => word.wordTypeUuid && word.wordTypeUuid.trim().length > 0
        )?.wordTypeUuid || '';
    }

    if (!wordTypeUuidToUse || wordTypeUuidToUse.trim().length === 0) {
      return { wordTypeEntries: [] };
    }

    const wordsWithTypeUuid = filteredWords.map((word) => ({
      ...word,
      wordTypeUuid: word.wordTypeUuid || wordTypeUuidToUse,
    }));

    const wordsByType = new Map<string, IWordDefinition[]>();
    wordsWithTypeUuid.forEach((word) => {
      const wordTypeUuid = word.wordTypeUuid!.trim();
      if (!wordsByType.has(wordTypeUuid)) {
        wordsByType.set(wordTypeUuid, []);
      }
      wordsByType.get(wordTypeUuid)!.push(word);
    });

    const wordTypeEntries = Array.from(wordsByType.entries()).map(
      ([wordTypeUuid, typeWords]) => {
        const firstWord = typeWords[0];
        const scope =
          firstWord.scope || SimulationConstants.KEYWORDS_DEFAULT_SCOPE;

        return {
          wordTypeUuid: wordTypeUuid,
          scope,
          scopeUuid: this.getScopeUuid(scope),
          words: typeWords.map((word) => {
            const entry: {
              uuid?: string;
              wordKey: string;
              wordValue?: string | null;
            } = {
              wordKey: word.key.trim(),
            };

            if (includeUuid && word.definitionUuid) {
              entry.uuid = word.definitionUuid;
            }

            if (word.value) {
              entry.wordValue = word.value.trim();
            }

            return entry;
          }),
        };
      }
    );

    return { wordTypeEntries };
  }

  private updateFormWithWordDetails(
    wordDetails: IGetWordDetailsResponse,
    forceReplace: boolean = false
  ): void {
    if (!wordDetails || !Array.isArray(wordDetails)) {
      return;
    }

    const replaceWordsGroup = wordDetails.find(
      (group) =>
        group.wordTypeName === SimulationConstants.REPLACE_WORDS_SECTION
    );

    if (!replaceWordsGroup) {
      return;
    }

    if (replaceWordsGroup.wordTypeUuid) {
      this.wordTypeUuid = replaceWordsGroup.wordTypeUuid;
      const wordTypeUuidControl = this.form?.get('replace_words_wordTypeUuid');
      if (wordTypeUuidControl) {
        wordTypeUuidControl.setValue(replaceWordsGroup.wordTypeUuid, {
          emitEvent: false,
        });
      }
    }

    if (replaceWordsGroup.hasProductScopedWords !== undefined) {
      const hasProductScopedWordsControl = this.form?.get(
        'replace_words_hasProductScopedWords'
      );
      if (hasProductScopedWordsControl) {
        hasProductScopedWordsControl.setValue(
          replaceWordsGroup.hasProductScopedWords,
          { emitEvent: false }
        );
      }
    }

    if (!replaceWordsGroup.words || replaceWordsGroup.words.length === 0) {
      this.isUpdatingFromAPI.set(true);

      // Clear the form when word details are empty
      if (this.replaceWordsFormArray) {
        while (this.replaceWordsFormArray.length > 0) {
          this.replaceWordsFormArray.removeAt(0, { emitEvent: false });
        }
      }

      // Clear the paired fields control
      this.replaceWordsPairedFieldsControl.setValue([], { emitEvent: false });
      this.previousPairedFields = [];

      this.isUpdatingFromAPI.set(false);
      return;
    }

    this.isUpdatingFromAPI.set(true);

    if (!this.replaceWordsFormArray) {
      this.isUpdatingFromAPI.set(false);
      return;
    }

    const apiWords: IWordDefinition[] = replaceWordsGroup.words.map((word) => ({
      key: word.wordKey,
      value: word.wordValue,
      scope: word.scope,
      typeName: replaceWordsGroup.wordTypeName,
      definitionUuid: word.uuid,
      wordTypeUuid: replaceWordsGroup.wordTypeUuid,
      scopeUuid: word.scopeUuid,
    }));

    // If this refresh is part of an explicit reset/detach action, fully replace UI state.
    if (forceReplace) {
      this.replaceWordsFormArray.clear({ emitEvent: false });
      apiWords.forEach((word) => {
        this.replaceWordsFormArray.push(
          this.fb.group({
            key: [word.key],
            value: [word.value],
            scope: [word.scope],
            typeName: [word.typeName],
            definitionUuid: [word.definitionUuid],
            wordTypeUuid: [word.wordTypeUuid],
            scopeUuid: [
              word.scopeUuid ||
                this.getScopeUuid(
                  word.scope || SimulationConstants.KEYWORDS_DEFAULT_SCOPE
                ),
            ],
          }),
          { emitEvent: false }
        );
      });

      this.syncFormArrayToControl();
      this.previousPairedFields =
        this.replaceWordsPairedFieldsControl.value || [];
      this.updateFormControlDisabledState();

      this.cdr.detectChanges();
      requestAnimationFrame(() => {
        this.isUpdatingFromAPI.set(false);
      });
      return;
    }

    const incompleteFields = (
      this.replaceWordsFormArray.getRawValue() as IWordDefinition[]
    ).filter((w) => w.key?.trim() && !w.value?.trim());
    const apiWordsByUuid = new Map(
      apiWords
        .filter((w) => w.definitionUuid)
        .map((w) => [w.definitionUuid!, w])
    );
    const apiWordsByKeyValue = new Map(
      apiWords
        .filter((w) => w.key?.trim() && w.value?.trim())
        .map((w) => [`${w.key?.trim()}|${w.value?.trim()}`, w])
    );
    const matchedApiWords = new Set<string>();

    for (let i = 0; i < this.replaceWordsFormArray.length; i++) {
      const formGroup = this.replaceWordsFormArray.at(i) as FormGroup;
      const word = formGroup.getRawValue() as IWordDefinition;
      const k = word.key?.trim() || '';
      const v = word.value?.trim() || '';
      const matched =
        word.definitionUuid && apiWordsByUuid.has(word.definitionUuid)
          ? apiWordsByUuid.get(word.definitionUuid)
          : k && v && apiWordsByKeyValue.has(`${k}|${v}`)
          ? apiWordsByKeyValue.get(`${k}|${v}`)
          : undefined;

      if (matched) {
        if (word.definitionUuid) matchedApiWords.add(word.definitionUuid);
        if (k && v) matchedApiWords.add(`${k}|${v}`);
        [
          'definitionUuid',
          'wordTypeUuid',
          'scopeUuid',
          'scope',
          'typeName',
        ].forEach((field) => {
          const control = formGroup.get(field);
          if (control && matched[field as keyof IWordDefinition]) {
            control.setValue(matched[field as keyof IWordDefinition], {
              emitEvent: false,
            });
          }
        });
      }
    }

    const existing = new Set(
      (this.replaceWordsFormArray.getRawValue() as IWordDefinition[]).map(
        (w) => `${w.key?.trim()}|${w.value?.trim()}`
      )
    );
    const apiKeys = new Set(apiWords.map((w) => w.key?.trim()).filter(Boolean));

    apiWords.forEach((word) => {
      const k = word.key?.trim() || '';
      const v = word.value?.trim() || '';
      if (!k || !v) return;
      if (word.definitionUuid && matchedApiWords.has(word.definitionUuid))
        return;
      if (matchedApiWords.has(`${k}|${v}`)) return;
      if (existing.has(`${k}|${v}`)) return;
      this.replaceWordsFormArray.push(
        this.fb.group({
          key: [word.key],
          value: [word.value],
          scope: [word.scope],
          typeName: [word.typeName],
          definitionUuid: [word.definitionUuid],
          wordTypeUuid: [word.wordTypeUuid],
          scopeUuid: [
            word.scopeUuid ||
              this.getScopeUuid(
                word.scope || SimulationConstants.KEYWORDS_DEFAULT_SCOPE
              ),
          ],
        }),
        { emitEvent: false }
      );
      existing.add(`${k}|${v}`);
    });

    incompleteFields.forEach((word) => {
      const k = word.key?.trim();
      if (!k || apiKeys.has(k)) return;
      if (existing.has(`${k}|`)) return;
      this.replaceWordsFormArray.push(
        this.fb.group({
          key: [word.key],
          value: [word.value || null],
          scope: [word.scope || SimulationConstants.KEYWORDS_DEFAULT_SCOPE],
          typeName: [word.typeName || replaceWordsGroup.wordTypeName],
          definitionUuid: [word.definitionUuid || ''],
          wordTypeUuid: [word.wordTypeUuid || replaceWordsGroup.wordTypeUuid],
          scopeUuid: [
            word.scopeUuid ||
              this.getScopeUuid(
                word.scope || SimulationConstants.KEYWORDS_DEFAULT_SCOPE
              ),
          ],
        }),
        { emitEvent: false }
      );
      existing.add(`${k}|`);
    });

    this.syncFormArrayToControl();
    this.updateFormControlDisabledState();

    this.previousPairedFields =
      this.replaceWordsPairedFieldsControl.value || [];

    this.cdr.detectChanges();
    requestAnimationFrame(() => {
      this.isUpdatingFromAPI.set(false);
    });
  }

  private createKeyword(words: IWordDefinition[]): void {
    if (!this.callUuid || words.length === 0) {
      return;
    }

    const payload = this.transformWordsToRequest(words, false);

    if (payload.wordTypeEntries.length === 0) {
      console.warn('No valid word type entries to create');
      return;
    }

    const keyValueKeys: string[] = [];
    words.forEach((word) => {
      const trimmedKey = word.key ? word.key.trim() : '';
      const trimmedValue = word.value ? word.value.trim() : '';
      const keyValueKey = `${trimmedKey}|${trimmedValue}`;
      if (keyValueKey && !this.inFlightCreateRequests.has(keyValueKey)) {
        this.inFlightCreateRequests.add(keyValueKey);
        keyValueKeys.push(keyValueKey);
      }
    });

    this.apiRequestCount.update((count) => count + 1);
    this.createUpdateApiCount.update((count) => count + 1);
    this.authorService
      .createWords(payload)
      .pipe(
        switchMap(() => {
          return this.authorService.getWordDetails(
            this.callUuid,
            this.personaUuid
          );
        })
      )
      .subscribe({
        next: (wordDetails: IGetWordDetailsResponse) => {
          console.log(
            'createKeyword successful, updating form with word details'
          );
          keyValueKeys.forEach((key) => {
            this.inFlightCreateRequests.delete(key);
          });
          words.forEach((word) => {
            const itemId =
              word.definitionUuid ||
              `${word.key?.trim()}|${word.value?.trim()}`;
            this.apiErrors.delete(itemId);
          });
          this.updateFormWithWordDetails(wordDetails);
          this.apiRequestCount.update((count) => Math.max(0, count - 1));
          this.createUpdateApiCount.update((count) => Math.max(0, count - 1));
        },
        error: (error: any) => {
          console.error(
            'Error creating keywords or fetching word details:',
            error
          );
          keyValueKeys.forEach((key) => {
            this.inFlightCreateRequests.delete(key);
          });
          if (error?.status === this.DUPLICATE_WORD_STATUS) {
            this.handleDuplicateError(words);
          }
          this.apiRequestCount.update((count) => Math.max(0, count - 1));
          this.createUpdateApiCount.update((count) => Math.max(0, count - 1));
        },
      });
  }

  private updateKeyword(words: IWordDefinition[]): void {
    if (!this.callUuid || words.length === 0) {
      return;
    }

    const payload = this.transformWordsToRequest(words, true);

    if (payload.wordTypeEntries.length === 0) {
      console.warn('No valid word type entries to update');
      return;
    }

    this.apiRequestCount.update((count) => count + 1);
    this.createUpdateApiCount.update((count) => count + 1);
    this.authorService.updateWords(payload).subscribe({
      next: (response: any) => {
        console.log('updateKeyword successful:', response);
        words.forEach((word) => {
          const itemId =
            word.definitionUuid || `${word.key?.trim()}|${word.value?.trim()}`;
          const error = this.apiErrors.get(itemId);
          if (
            error &&
            !(
              error.key === this.ERROR_DUPLICATE_KEY_VALUE &&
              error.value === this.ERROR_DUPLICATE_KEY_VALUE
            )
          ) {
            this.apiErrors.delete(itemId);
          }
        });
        this.replaceWordsPairedFieldsControl.updateValueAndValidity();
        this.apiRequestCount.update((count) => Math.max(0, count - 1));
        this.createUpdateApiCount.update((count) => Math.max(0, count - 1));
      },
      error: (error: any) => {
        console.error('Error updating keywords:', error);
        if (error?.status === this.DUPLICATE_WORD_STATUS) {
          this.handleDuplicateError(words);
        }
        this.apiRequestCount.update((count) => Math.max(0, count - 1));
        this.createUpdateApiCount.update((count) => Math.max(0, count - 1));
      },
    });
  }

  private processReplaceWordsForAPI(): void {
    if (!this.replaceWordsFormArray) return;

    const replaceWords =
      this.replaceWordsFormArray.getRawValue() as IWordDefinition[];

    // Only process words that have both key and value (complete pairs)
    const validWords = replaceWords.filter(
      (word) =>
        word.key &&
        word.key.trim().length > 0 &&
        word.value &&
        word.value.trim().length > 0
    );

    if (validWords.length === 0) {
      return;
    }

    const wordsToUpdate: IWordDefinition[] = [];
    const wordsToCreate: IWordDefinition[] = [];
    const processedUuids = new Set<string>();
    const processedKeyValues = new Set<string>();

    validWords.forEach((word) => {
      const trimmedKey = word.key.trim();
      const trimmedValue = word.value ? word.value.trim() : '';
      const keyValueKey = `${trimmedKey}|${trimmedValue}`;

      if (word.definitionUuid && word.definitionUuid.trim().length > 0) {
        if (processedUuids.has(word.definitionUuid)) {
          wordsToCreate.push({ ...word, definitionUuid: '' });
          return;
        }
        if (processedKeyValues.has(keyValueKey)) {
          wordsToCreate.push({ ...word, definitionUuid: '' });
          return;
        }
        wordsToUpdate.push(word);
        processedUuids.add(word.definitionUuid);
        processedKeyValues.add(keyValueKey);
      } else {
        if (processedKeyValues.has(keyValueKey)) {
          return;
        }

        if (this.inFlightCreateRequests.has(keyValueKey)) {
          return;
        }

        wordsToCreate.push(word);
        processedKeyValues.add(keyValueKey);
      }
    });

    if (wordsToUpdate.length > 0) {
      this.updateKeyword(wordsToUpdate);
    }

    if (wordsToCreate.length > 0) {
      this.createKeyword(wordsToCreate);
    }
  }

  onResetToProductDefault(): void {
    if (this.callUuid) {
      const wordType: CallWordType = 'replace_words';
      this.apiRequestCount.update((count) => count + 1);
      this.authorService
        .resetCallKeywords(this.callUuid, wordType)
        .pipe(
          switchMap(() => {
            return this.authorService.getWordDetails(
              this.callUuid,
              this.personaUuid
            );
          })
        )
        .subscribe({
          next: (wordDetails: IGetWordDetailsResponse) => {
            this.updateFormWithWordDetails(wordDetails, true);
            this.apiRequestCount.update((count) => Math.max(0, count - 1));
          },
          error: (error: any) => {
            console.error(
              'Error resetting to product default or fetching word details:',
              error
            );
            this.apiRequestCount.update((count) => Math.max(0, count - 1));
          },
        });
    }
  }

  saveAsProductDefault(): void {
    this.saveAsProductDefaultModalOpen = true;
  }

  onSaveAsProductDefaultConfirm(): void {
    if (!this.callUuid || !this.moduleUuid) {
      console.error('Missing callUuid or moduleUuid');
      return;
    }

    this.networkRequestInProgress = true;
    this.apiRequestCount.update((count) => count + 1);

    this.authorService
      .saveAsProductDefault({
        moduleUuid: this.moduleUuid,
        wordType: 'replace_words' as CallWordType,
        callUuid: this.callUuid,
        personaUuid: this.personaUuid,
      })
      .pipe(
        switchMap(() => {
          return this.authorService.getWordDetails(
            this.callUuid,
            this.personaUuid
          );
        })
      )
      .subscribe({
        next: (wordDetails: IGetWordDetailsResponse) => {
          console.log(
            'Save as product default successful, updating form with word details'
          );
          this.updateFormWithWordDetails(wordDetails, true);
          this.networkRequestInProgress = false;
          this.apiRequestCount.update((count) => Math.max(0, count - 1));
          this.saveAsProductDefaultModalOpen = false;
        },
        error: (error: any) => {
          console.error(
            'Error saving as product default or fetching word details:',
            error
          );
          this.networkRequestInProgress = false;
          this.apiRequestCount.update((count) => Math.max(0, count - 1));
        },
      });
  }

  onSaveAsProductDefaultModalClose(): void {
    this.saveAsProductDefaultModalOpen = false;
  }

  onDetachFromProductDefault(): void {
    if (this.callUuid && this.moduleUuid) {
      this.apiRequestCount.update((count) => count + 1);
      this.authorService
        .cloneModuleKeywords({
          callUuid: this.callUuid,
          moduleUuid: this.moduleUuid,
          wordType: 'replace_words' as CallWordType,
        })
        .pipe(
          switchMap(() => {
            return this.authorService.getWordDetails(
              this.callUuid,
              this.personaUuid
            );
          })
        )
        .subscribe({
          next: (wordDetails: IGetWordDetailsResponse) => {
            console.log(
              'Detach from product default successful, updating form with word details'
            );
            this.updateFormWithWordDetails(wordDetails, true);
            this.apiRequestCount.update((count) => Math.max(0, count - 1));
          },
          error: (error: any) => {
            console.error(
              'Error detaching from product default or fetching word details:',
              error
            );
            this.apiRequestCount.update((count) => Math.max(0, count - 1));
          },
        });
    }
  }

  private loadPersonaUuid(): void {
    this.authorReactiveFormService
      .getForm(this.callUuid)
      .pipe(take(1))
      .subscribe((form: FormGroup) => {
        const generalSection = form.get(
          SimulationConstants.GENERAL_SECTION
        ) as FormGroup;
        const callPersonaGroup = generalSection?.get(
          SimulationConstants.CALL_PERSONA_SECTION
        ) as FormGroup;
        const personaUuidControl = callPersonaGroup?.get('uuid');
        if (personaUuidControl) {
          this.personaUuid = personaUuidControl.value || '';
        }
      });
  }

  private loadModuleUuid(): void {
    this.authorReactiveFormService
      .getForm(this.callUuid)
      .pipe(take(1))
      .subscribe((form: FormGroup) => {
        const generalSection = form.get(
          SimulationConstants.GENERAL_SECTION
        ) as FormGroup;
        const moduleUuidControl = generalSection?.get('moduleUuid');
        if (moduleUuidControl) {
          this.moduleUuid = moduleUuidControl.value || '';
        }
      });
  }

  private detectRemovals(
    previous: PairedFieldItem[],
    current: PairedFieldItem[]
  ): PairedFieldItem[] {
    if (!previous || !current || previous.length === 0) {
      return [];
    }

    // Create maps for efficient lookup
    const currentMapByUuid = new Map<string, PairedFieldItem>();
    const currentMapByKeyValue = new Map<string, PairedFieldItem>();
    const currentMapByKey = new Map<string, PairedFieldItem>();
    // Map by value only - for matching empty-key items that get filled in
    const currentMapByValue = new Map<string, PairedFieldItem[]>();
    const currentKeyCounts = new Map<string, number>();

    current.forEach((item) => {
      const trimmedKey = item.key.trim();
      const trimmedValue = (item.value || '').trim();

      // Map by UUID if available
      if (item.uuid) {
        currentMapByUuid.set(item.uuid, item);
      }

      // Map by key+value combination
      const keyValueKey = `key:${trimmedKey}|value:${trimmedValue}`;
      if (!currentMapByKeyValue.has(keyValueKey)) {
        currentMapByKeyValue.set(keyValueKey, item);
      }

      // Map by key only (for tracking same key with different values)
      if (!currentMapByKey.has(trimmedKey)) {
        currentMapByKey.set(trimmedKey, item);
      }
      currentKeyCounts.set(
        trimmedKey,
        (currentKeyCounts.get(trimmedKey) || 0) + 1
      );

      // Map by value only (for matching empty-key items that get filled in)
      if (trimmedValue) {
        if (!currentMapByValue.has(trimmedValue)) {
          currentMapByValue.set(trimmedValue, []);
        }
        currentMapByValue.get(trimmedValue)!.push(item);
      }
    });

    const previousKeyCounts = new Map<string, number>();
    previous.forEach((item) => {
      const trimmedKey = item.key.trim();
      previousKeyCounts.set(
        trimmedKey,
        (previousKeyCounts.get(trimmedKey) || 0) + 1
      );
    });

    // Find items in previous that are truly removed
    const removals: PairedFieldItem[] = [];
    previous.forEach((item) => {
      const trimmedKey = item.key.trim();
      const trimmedValue = (item.value || '').trim();
      const keyValueKey = `key:${trimmedKey}|value:${trimmedValue}`;

      if (item.uuid) {
        if (!currentMapByUuid.has(item.uuid)) {
          // A lower key count means this row was removed (its key survives on an unsaved duplicate), not value-edited in place
          const keyCountDropped =
            (currentKeyCounts.get(trimmedKey) || 0) <
            (previousKeyCounts.get(trimmedKey) || 0);
          if (!currentMapByKey.has(trimmedKey) || keyCountDropped) {
            removals.push(item);
          }
        }
      } else {
        // No UUID - check by key+value combination
        if (!currentMapByKeyValue.has(keyValueKey)) {
          // Key+value not found - check if same key exists
          if (!currentMapByKey.has(trimmedKey)) {
            if (trimmedKey === '' && trimmedValue) {
              const itemsWithSameValue = currentMapByValue.get(trimmedValue);
              if (itemsWithSameValue && itemsWithSameValue.length > 0) {
                return;
              }
            }
            removals.push(item);
          }
        }
      }
    });

    return removals;
  }

  private onItemsRemoved(removedItems: PairedFieldItem[]): void {
    if (
      !this.replaceWordsFormArray ||
      !removedItems ||
      removedItems.length === 0
    ) {
      return;
    }

    const currentWords =
      this.replaceWordsFormArray.getRawValue() as IWordDefinition[];
    const wordsToDelete: Array<{
      word: IWordDefinition;
      index: number;
    }> = [];

    // Find all words that match the removed items
    removedItems.forEach((removedItem) => {
      // If UUID is provided, use it directly to find the exact word (handles duplicates)
      if (removedItem.uuid && removedItem.uuid.trim().length > 0) {
        const index = currentWords.findIndex(
          (word) =>
            word.definitionUuid &&
            word.definitionUuid.trim() === removedItem.uuid!.trim()
        );
        if (index >= 0) {
          wordsToDelete.push({
            word: currentWords[index],
            index: index,
          });
        }
      } else {
        // If no UUID, match by key and value
        const matches: Array<{ word: IWordDefinition; index: number }> = [];
        currentWords.forEach((word, index) => {
          if (
            word.key &&
            word.key.trim() === removedItem.key.trim() &&
            word.value &&
            word.value.trim() === (removedItem.value || '').trim()
          ) {
            matches.push({ word, index });
          }
        });

        // Match a uuid-less removed row to an unsaved FormArray row first so a persisted word is never deleted for it
        const unsavedMatch = matches.find(
          ({ word }) =>
            !word.definitionUuid || word.definitionUuid.trim().length === 0
        );
        const match = unsavedMatch || matches[0];
        if (match) {
          wordsToDelete.push(match);
        }
      }
    });

    if (wordsToDelete.length === 0) {
      return;
    }

    // Group words by scopeUuid for batch deletion
    const wordsByScope = new Map<string, string[]>();
    const uuidSet = new Set<string>();

    // Skip the API delete when another remaining row still carries this uuid
    const removedIndexes = new Set(wordsToDelete.map(({ index }) => index));
    const remainingUuids = new Set<string>();
    currentWords.forEach((word, index) => {
      if (
        !removedIndexes.has(index) &&
        word.definitionUuid &&
        word.definitionUuid.trim().length > 0
      ) {
        remainingUuids.add(word.definitionUuid.trim());
      }
    });

    wordsToDelete.forEach(({ word }) => {
      if (
        word.definitionUuid &&
        word.definitionUuid.trim().length > 0 &&
        !uuidSet.has(word.definitionUuid) &&
        !remainingUuids.has(word.definitionUuid.trim())
      ) {
        uuidSet.add(word.definitionUuid);
        const scopeUuid =
          word.scopeUuid ||
          this.getScopeUuid(
            word.scope || SimulationConstants.KEYWORDS_DEFAULT_SCOPE
          );

        if (scopeUuid && scopeUuid.trim().length > 0) {
          if (!wordsByScope.has(scopeUuid)) {
            wordsByScope.set(scopeUuid, []);
          }
          wordsByScope.get(scopeUuid)!.push(word.definitionUuid);
        }
      }
    });

    wordsByScope.forEach((uuids, scopeValue) => {
      this.deleteWords(uuids, scopeValue);
    });

    this.isUpdatingFromFormArray.set(true);
    wordsToDelete
      .sort((a, b) => b.index - a.index)
      .forEach(({ index }) => {
        this.replaceWordsFormArray.removeAt(index, { emitEvent: false });
      });
    this.isUpdatingFromFormArray.set(false);

    this.syncFormArrayToControl();
    this.finalizeRemoval();
  }

  onPairRemoved(removedItem: PairedFieldItem): void {
    if (!this.replaceWordsFormArray || !removedItem || !removedItem.key) {
      return;
    }

    const currentWords =
      this.replaceWordsFormArray.getRawValue() as IWordDefinition[];

    const wordsToDelete: Array<{
      word: IWordDefinition;
      index: number;
    }> = [];

    if (removedItem.uuid && removedItem.uuid.trim().length > 0) {
      currentWords.forEach((word, index) => {
        if (
          word.definitionUuid &&
          word.definitionUuid.trim() === removedItem.uuid!.trim()
        ) {
          wordsToDelete.push({ word, index });
        }
      });
    } else {
      const trimmedKey = removedItem.key.trim();
      const trimmedValue = (removedItem.value || '').trim();

      currentWords.forEach((word, index) => {
        const wordKey = word.key ? word.key.trim() : '';
        const wordValue = word.value ? word.value.trim() : '';
        if (wordKey === trimmedKey && wordValue === trimmedValue) {
          wordsToDelete.push({ word, index });
        }
      });
    }

    if (wordsToDelete.length === 0) {
      // Clear stale duplicate errors even when the removed row was never synced to the FormArray
      this.validateAndUpdateErrors();
      this.replaceWordsPairedFieldsControl.updateValueAndValidity();
      this.updateFormControlDisabledState();
      const currentPairedFields =
        this.replaceWordsPairedFieldsControl.value || [];
      this.previousPairedFields = currentPairedFields.filter(
        (item) =>
          !(item.key === removedItem.key && item.value === removedItem.value)
      );
      return;
    }

    const wordsByScope = new Map<string, string[]>();
    const uuidSet = new Set<string>();

    // Skip the API delete when another remaining row still carries this uuid
    const removedIndexes = new Set(wordsToDelete.map(({ index }) => index));
    const remainingUuids = new Set<string>();
    currentWords.forEach((word, index) => {
      if (
        !removedIndexes.has(index) &&
        word.definitionUuid &&
        word.definitionUuid.trim().length > 0
      ) {
        remainingUuids.add(word.definitionUuid.trim());
      }
    });

    wordsToDelete.forEach(({ word }) => {
      if (
        word.definitionUuid &&
        word.definitionUuid.trim().length > 0 &&
        !uuidSet.has(word.definitionUuid) &&
        !remainingUuids.has(word.definitionUuid.trim())
      ) {
        uuidSet.add(word.definitionUuid);
        const scopeUuid =
          word.scopeUuid ||
          this.getScopeUuid(
            word.scope || SimulationConstants.KEYWORDS_DEFAULT_SCOPE
          );

        if (scopeUuid && scopeUuid.trim().length > 0) {
          if (!wordsByScope.has(scopeUuid)) {
            wordsByScope.set(scopeUuid, []);
          }
          wordsByScope.get(scopeUuid)!.push(word.definitionUuid);
        }
      }
    });

    wordsByScope.forEach((uuids, scopeValue) => {
      this.deleteWords(uuids, scopeValue);
    });

    this.isUpdatingFromFormArray.set(true);
    wordsToDelete
      .sort((a, b) => b.index - a.index)
      .forEach(({ index }) => {
        this.replaceWordsFormArray.removeAt(index, { emitEvent: false });
      });
    this.isUpdatingFromFormArray.set(false);

    this.syncFormArrayToControl();
    this.finalizeRemoval();
  }

  // Clears stale duplicate errors after a removal and re-baselines the debounced sync so it doesn't re-save every remaining row
  private finalizeRemoval(): void {
    this.validateAndUpdateErrors();
    this.replaceWordsPairedFieldsControl.updateValueAndValidity();
    this.updateFormControlDisabledState();
    this.previousPairedFields =
      this.replaceWordsPairedFieldsControl.value || [];
  }

  private deleteWords(uuids: string[], scopeValue: string): void {
    if (uuids.length === 0 || !scopeValue) {
      return;
    }

    let didRefetch = false;

    this.apiRequestCount.update((count) => count + 1);
    this.deleteApiCount.update((count) => count + 1);
    this.authorService
      .deleteWords({
        uuids,
        scopeValue,
      })
      .pipe(
        switchMap(() => {
          // Decide from the post-removal FormArray, after the delete has succeeded
          const remainingWords =
            (this.replaceWordsFormArray?.getRawValue() as IWordDefinition[]) ||
            [];
          const hasPersistedWords = remainingWords.some(
            (word) =>
              word.definitionUuid &&
              word.definitionUuid.trim().length > 0 &&
              !uuids.includes(word.definitionUuid)
          );
          // Create a surviving complete unsaved pair (a former duplicate) instead of wiping it with the product-default refetch
          const completeUnsavedWords = remainingWords.filter(
            (word) =>
              (!word.definitionUuid ||
                word.definitionUuid.trim().length === 0) &&
              word.key &&
              word.key.trim().length > 0 &&
              word.value &&
              word.value.trim().length > 0
          );
          if (completeUnsavedWords.length > 0) {
            this.createKeyword(completeUnsavedWords);
          }
          if (!hasPersistedWords && completeUnsavedWords.length === 0) {
            didRefetch = true;
            return this.authorService.getWordDetails(
              this.callUuid,
              this.personaUuid
            );
          }
          return of([]);
        })
      )
      .subscribe({
        next: (wordDetails: IGetWordDetailsResponse | any[]) => {
          if (didRefetch && Array.isArray(wordDetails)) {
            console.log(
              'Delete words successful, updating form with word details'
            );
            this.updateFormWithWordDetails(wordDetails, true);
          } else {
            console.log('Delete words successful');
          }
          this.apiRequestCount.update((count) => Math.max(0, count - 1));
          this.deleteApiCount.update((count) => Math.max(0, count - 1));
        },
        error: (error: any) => {
          console.error('Error deleting words:', error);
          this.apiRequestCount.update((count) => Math.max(0, count - 1));
          this.deleteApiCount.update((count) => Math.max(0, count - 1));
        },
      });
  }

  private validateAndUpdateErrors(): void {
    const currentWords =
      this.replaceWordsFormArray.getRawValue() as IWordDefinition[];
    const duplicateFields = this.detectDuplicateKeyValuePairs(currentWords);

    const uuidToKeyValue = new Map<string, string>();
    const currentKeyValuePairs = new Set<string>();
    const currentItemIds = new Set<string>();

    currentWords.forEach((word) => {
      const trimmedKey = word.key?.trim() || '';
      const trimmedValue = word.value?.trim() || '';
      if (trimmedKey && trimmedValue) {
        const keyValueKey = `${trimmedKey}|${trimmedValue}`;
        currentKeyValuePairs.add(keyValueKey);
        const itemId = word.definitionUuid || keyValueKey;
        currentItemIds.add(itemId);
        if (word.definitionUuid) {
          uuidToKeyValue.set(word.definitionUuid, keyValueKey);
        }
      }
    });

    const itemsToClear: string[] = [];
    this.apiErrors.forEach((error, itemId) => {
      const isUuid = !itemId.includes('|');

      if (isUuid) {
        if (!currentItemIds.has(itemId)) {
          itemsToClear.push(itemId);
        } else {
          const currentKeyValue = uuidToKeyValue.get(itemId);
          if (currentKeyValue && !duplicateFields.has(currentKeyValue)) {
            if (
              error.key === this.ERROR_DUPLICATE_KEY_VALUE ||
              error.value === this.ERROR_DUPLICATE_KEY_VALUE
            ) {
              itemsToClear.push(itemId);
            }
          }
        }
      } else {
        if (!currentKeyValuePairs.has(itemId)) {
          itemsToClear.push(itemId);
        } else if (!duplicateFields.has(itemId)) {
          if (
            error.key === this.ERROR_DUPLICATE_KEY_VALUE ||
            error.value === this.ERROR_DUPLICATE_KEY_VALUE
          ) {
            itemsToClear.push(itemId);
          }
        }
      }
    });

    itemsToClear.forEach((itemId) => this.apiErrors.delete(itemId));

    this.duplicateKeyValuePairs.clear();
    duplicateFields.forEach((keyValueKey) =>
      this.duplicateKeyValuePairs.add(keyValueKey)
    );

    duplicateFields.forEach((keyValueKey) => {
      currentWords.forEach((word) => {
        const trimmedKey = word.key?.trim() || '';
        const trimmedValue = word.value?.trim() || '';
        if (`${trimmedKey}|${trimmedValue}` === keyValueKey) {
          const itemId = word.definitionUuid || keyValueKey;
          if (!this.apiErrors.has(itemId)) {
            this.apiErrors.set(itemId, {
              key: this.ERROR_DUPLICATE_KEY_VALUE,
              value: this.ERROR_DUPLICATE_KEY_VALUE,
            });
          }
        }
      });
    });
  }

  private clearErrorsForModifiedFields(currentItems: PairedFieldItem[]): void {
    const currentItemIds = new Set<string>();
    const currentKeyValuePairs = new Set<string>();

    currentItems.forEach((item) => {
      const trimmedKey = item.key?.trim() || '';
      const trimmedValue = item.value?.trim() || '';
      if (trimmedKey && trimmedValue) {
        const keyValueKey = `${trimmedKey}|${trimmedValue}`;
        currentKeyValuePairs.add(keyValueKey);
        const itemId = item.uuid || keyValueKey;
        currentItemIds.add(itemId);
      }
    });

    const itemsToClear: string[] = [];
    this.apiErrors.forEach((error, itemId) => {
      const isDuplicateError =
        error.key === this.ERROR_DUPLICATE_KEY_VALUE &&
        error.value === this.ERROR_DUPLICATE_KEY_VALUE;

      if (isDuplicateError) {
        return;
      }

      const isUuid = !itemId.includes('|');

      if (isUuid) {
        if (!currentItemIds.has(itemId)) {
          itemsToClear.push(itemId);
        }
      } else {
        if (!currentKeyValuePairs.has(itemId)) {
          itemsToClear.push(itemId);
        }
      }
    });

    itemsToClear.forEach((itemId) => this.apiErrors.delete(itemId));
  }

  private handleDuplicateError(words: IWordDefinition[]): void {
    const allFormWords =
      (this.replaceWordsFormArray?.getRawValue() as IWordDefinition[]) || [];
    const duplicateKeyValuePairs = this.detectDuplicateKeyValuePairs(
      words,
      allFormWords
    );

    if (duplicateKeyValuePairs.size > 0) {
      duplicateKeyValuePairs.forEach((keyValueKey) =>
        this.duplicateKeyValuePairs.add(keyValueKey)
      );
      this.setErrorsOnDuplicateFields(
        duplicateKeyValuePairs,
        allFormWords,
        words
      );
    }

    this.replaceWordsPairedFieldsControl.updateValueAndValidity();
  }

  private setErrorsOnDuplicateFields(
    duplicateKeyValuePairs: Set<string>,
    allFormWords: IWordDefinition[],
    requestWords: IWordDefinition[]
  ): void {
    const allWordsToCheck = [...allFormWords, ...requestWords];

    duplicateKeyValuePairs.forEach((keyValueKey) => {
      allWordsToCheck.forEach((word) => {
        const trimmedKey = word.key?.trim() || '';
        const trimmedValue = word.value?.trim() || '';
        const wordKeyValueKey = `${trimmedKey}|${trimmedValue}`;

        if (wordKeyValueKey === keyValueKey) {
          const itemId = word.definitionUuid || wordKeyValueKey;
          this.apiErrors.set(itemId, {
            key: this.ERROR_DUPLICATE_KEY_VALUE,
            value: this.ERROR_DUPLICATE_KEY_VALUE,
          });
        }
      });
    });
  }

  private detectDuplicateKeyValuePairs(
    words: IWordDefinition[],
    allFormWords?: IWordDefinition[]
  ): Set<string> {
    const duplicates = new Set<string>();
    const formWords =
      allFormWords && allFormWords !== words
        ? allFormWords
        : (this.replaceWordsFormArray?.getRawValue() as IWordDefinition[]) ||
          [];

    const keyValueCounts = new Map<string, number>();
    const keyCounts = new Map<string, number>();

    formWords.forEach((w) => {
      const trimmedKey = w.key?.trim() || '';
      const trimmedValue = w.value?.trim() || '';
      if (trimmedKey && trimmedValue) {
        const keyValueKey = `${trimmedKey}|${trimmedValue}`;
        keyValueCounts.set(
          keyValueKey,
          (keyValueCounts.get(keyValueKey) || 0) + 1
        );
        keyCounts.set(trimmedKey, (keyCounts.get(trimmedKey) || 0) + 1);
      }
    });

    keyValueCounts.forEach((count, keyValueKey) => {
      if (count > 1) {
        duplicates.add(keyValueKey);
      }
    });

    keyCounts.forEach((count, key) => {
      if (count > 1) {
        formWords.forEach((w) => {
          const trimmedKey = w.key?.trim() || '';
          const trimmedValue = w.value?.trim() || '';
          if (trimmedKey === key && trimmedValue) {
            const keyValueKey = `${trimmedKey}|${trimmedValue}`;
            duplicates.add(keyValueKey);
          }
        });
      }
    });

    return duplicates;
  }

  ngOnDestroy(): void {
    if (this.replaceWordsSubscription) {
      this.replaceWordsSubscription.unsubscribe();
    }
    if (this.replaceWordsPairedFieldsControlSubscription) {
      this.replaceWordsPairedFieldsControlSubscription.unsubscribe();
    }
  }
}
